import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  User, PencilSimple, Key, Bell, Share, Crown,
  Check, X, Copy, TwitterLogo, InstagramLogo, LinkedinLogo, Globe,
  ArrowLeft, CurrencyDollar, Users, Camera, UploadSimple, Cross
} from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;
const REFERRAL_COMMISSION = 20;

const ProfilePage = () => {
  const { user, getAuthHeader, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referralList, setReferralList] = useState([]);
  
  // Photo positioning state
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [photoPosition, setPhotoPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const photoEditorRef = useRef(null);
  
  const [profileForm, setProfileForm] = useState({
    name: '',
    bio: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    social_website: ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [notifications, setNotifications] = useState({
    notification_email_marketing: true,
    notification_email_community: true,
    notification_email_updates: true,
    devotional_email_enabled: false
  });

  useEffect(() => {
    fetchProfile();
    fetchReferralData();
    fetchDevotionalStatus();
  }, []);

  const fetchDevotionalStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/devotional/subscription-status`, {
        headers: getAuthHeader()
      });
      setNotifications(prev => ({
        ...prev,
        devotional_email_enabled: response.data.subscribed
      }));
    } catch (error) {
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/profile`, {
        headers: getAuthHeader()
      });
      setProfile(response.data);
      setProfileForm({
        name: response.data.name || '',
        bio: response.data.bio || '',
        social_twitter: response.data.social_twitter || '',
        social_instagram: response.data.social_instagram || '',
        social_linkedin: response.data.social_linkedin || '',
        social_website: response.data.social_website || ''
      });
      setNotifications({
        notification_email_marketing: response.data.notification_email_marketing ?? true,
        notification_email_community: response.data.notification_email_community ?? true,
        notification_email_updates: response.data.notification_email_updates ?? true
      });
    } catch (error) {
    }
  };

  const fetchReferralData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/api/referrals/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/referrals/list`, { headers: getAuthHeader() })
      ]);
      setReferralStats(statsRes.data);
      setReferralList(listRes.data);
    } catch (error) {
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/api/profile`, profileForm, {
        headers: getAuthHeader()
      });
      toast.success('Profile updated successfully');
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/profile`, notifications, {
        headers: getAuthHeader()
      });
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to update notifications');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/api/profile/upload-photo`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Profile photo updated!');
      fetchProfile();
      if (refreshUser) refreshUser();
      // Open editor after upload to position the photo
      setShowPhotoEditor(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Photo position handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = (e.clientX - dragStart.x) * 0.5;
    const deltaY = (e.clientY - dragStart.y) * 0.5;
    
    setPhotoPosition(prev => ({
      x: Math.max(0, Math.min(100, prev.x + deltaX)),
      y: Math.max(0, Math.min(100, prev.y + deltaY))
    }));
    
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    
    const deltaX = (touch.clientX - dragStart.x) * 0.5;
    const deltaY = (touch.clientY - dragStart.y) * 0.5;
    
    setPhotoPosition(prev => ({
      x: Math.max(0, Math.min(100, prev.x + deltaX)),
      y: Math.max(0, Math.min(100, prev.y + deltaY))
    }));
    
    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const savePhotoPosition = async () => {
    try {
      await axios.put(`${API}/api/profile/photo-position`, {
        position_x: photoPosition.x,
        position_y: photoPosition.y
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Photo position saved!');
      setShowPhotoEditor(false);
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save photo position');
    }
  };

  // Load saved position when profile loads
  useEffect(() => {
    if (profile?.photo_position_x !== undefined && profile?.photo_position_y !== undefined) {
      setPhotoPosition({
        x: profile.photo_position_x,
        y: profile.photo_position_y
      });
    }
  }, [profile]);

  const getProfilePhotoUrl = () => {
    if (profile?.profile_photo_path) {
      return `${API}/api/profile/photo/${profile.profile_photo_path}?auth=${token}`;
    }
    return null;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/profile/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: getAuthHeader()
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (referralStats?.referral_link) {
      navigator.clipboard.writeText(referralStats.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Key },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'referrals', label: 'Referrals', icon: Share }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F0E6]">
      {/* Header - Dark */}
      <header className="nav-glass border-b border-white/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/community" className="p-2 hover:bg-white/5 transition-colors text-white/60 hover:text-[#F4F0E6]">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-[#C4A661]">Settings</h1>
                <p className="text-sm text-white/40">Manage your account</p>
              </div>
            </div>
            {profile?.role === 'paid_member' && (
              <div className="flex items-center gap-2 bg-[#C4A661]/10 border border-[#C4A661]/30 px-4 py-2">
                <Crown size={16} weight="fill" className="text-[#C4A661]" />
                <span className="text-sm text-[#C4A661] font-bold">Inner Circle</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Scripture Banner - Gold */}
      <div className="bg-[#C4A661] py-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#0A0A0A] text-sm font-medium">
            <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
            "Whatever you do, work heartily, as for the Lord and not for men." — Colossians 3:23
          </p>
        </div>
      </div>

      {/* Main Content - Light Section */}
      <div className="bg-[#FDFDFD] py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-12 lg:px-24 max-w-4xl">
          {/* Page Title */}
          <div className="text-center mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C4A661] mb-2">Your Account</p>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0A0A0A]">Profile & <span className="text-[#C4A661]">Settings</span></h2>
          </div>

          {/* Tabs - Scrollable on mobile */}
          <div className="flex gap-2 mb-8 md:mb-10 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:justify-center scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 font-medium transition-all whitespace-nowrap text-sm md:text-base ${
                  activeTab === tab.id
                    ? 'bg-[#0A0A0A] text-[#F7F7F5]'
                    : 'bg-[#F5F5F0] border border-[#E5E5E5] text-[#525252] hover:text-[#0A0A0A] hover:border-[#C4A661]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Profile Photo Section */}
            <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2 text-[#0A0A0A]">
                <Camera size={20} className="text-[#C4A661]" />
                Profile Photo
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
                <div className="relative shrink-0">
                  {getProfilePhotoUrl() ? (
                    <div className="w-28 h-28 sm:w-24 sm:h-24 overflow-hidden border-2 border-[#C4A661]/30">
                      <img 
                        src={getProfilePhotoUrl()} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: `${profile?.photo_position_x ?? 50}% ${profile?.photo_position_y ?? 50}%`
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-28 h-28 sm:w-24 sm:h-24 bg-gradient-to-br from-[#C4A661] to-amber-600 flex items-center justify-center text-white text-4xl sm:text-3xl font-bold">
                      {profile?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <Camera size={28} className="text-white" weight="fill" />
                    )}
                  </button>
                </div>
                
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="btn-outline-light w-full sm:w-auto"
                      data-testid="upload-photo-btn"
                    >
                      <UploadSimple size={18} className="mr-2" />
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                    {getProfilePhotoUrl() && (
                      <Button
                        type="button"
                        onClick={() => setShowPhotoEditor(true)}
                        className="btn-outline-light w-full sm:w-auto"
                        data-testid="adjust-photo-btn"
                      >
                        <PencilSimple size={18} className="mr-2" />
                        Adjust Position
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-[#525252] mt-3">
                    JPEG, PNG, GIF, or WEBP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Edit Profile Form */}
            <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2 text-[#0A0A0A]">
                <PencilSimple size={20} className="text-[#C4A661]" />
                Edit Profile
              </h2>
              
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <Label className="text-[#525252]">Name</Label>
                    <Input
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A] focus:border-[#C4A661]"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <Label className="text-[#525252]">Email</Label>
                    <Input
                      value={profile?.email || ''}
                      disabled
                      className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#A3A3A3]"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-[#525252]">Bio</Label>
                  <Textarea
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                    className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A] min-h-[100px] focus:border-[#C4A661]"
                    placeholder="Tell us about yourself..."
                  />
                </div>
                
                <div>
                  <Label className="text-[#525252] mb-4 block">Social Links</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <TwitterLogo size={20} className="text-[#A3A3A3] shrink-0" />
                      <Input
                        value={profileForm.social_twitter}
                        onChange={(e) => setProfileForm({ ...profileForm, social_twitter: e.target.value })}
                        className="bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                        placeholder="@username"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <InstagramLogo size={20} className="text-[#A3A3A3] shrink-0" />
                      <Input
                        value={profileForm.social_instagram}
                        onChange={(e) => setProfileForm({ ...profileForm, social_instagram: e.target.value })}
                        className="bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                        placeholder="@username"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <LinkedinLogo size={20} className="text-[#A3A3A3] shrink-0" />
                      <Input
                        value={profileForm.social_linkedin}
                        onChange={(e) => setProfileForm({ ...profileForm, social_linkedin: e.target.value })}
                        className="bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                        placeholder="linkedin.com/in/..."
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe size={20} className="text-[#A3A3A3] shrink-0" />
                      <Input
                        value={profileForm.social_website}
                        onChange={(e) => setProfileForm({ ...profileForm, social_website: e.target.value })}
                        className="bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                        placeholder="yourwebsite.com"
                      />
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold"
                  data-testid="save-profile-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </div>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="bg-white border border-[#E5E5E5] p-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2 text-[#0A0A0A]">
                <Key size={20} className="text-[#C4A661]" />
                Change Password
              </h2>
              
              <form onSubmit={handleChangePassword} className="space-y-5 max-w-md">
                <div>
                  <Label className="text-[#525252]">Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#525252]">New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#525252]">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    className="mt-2 bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A]"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold"
                  data-testid="change-password-btn"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </div>
            
            {/* Subscription Management */}
            {profile && (
              <div className="bg-white border border-[#E5E5E5] p-8">
                <h2 className="text-lg font-bold mb-6 text-[#0A0A0A]">Subscription</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#525252]">
                      Status: <span className={profile.role === 'paid_member' ? 'text-[#C4A661] font-bold' : 'text-[#A3A3A3]'}>
                        {profile.role === 'paid_member' ? 'Inner Circle Member' : 'Free Member'}
                      </span>
                    </p>
                    {profile.subscription_status && (
                      <p className="text-sm text-[#A3A3A3] mt-1">
                        Subscription: {profile.subscription_status}
                      </p>
                    )}
                  </div>
                  {profile.role !== 'paid_member' && (
                    <Link to="/inner-circle-sales">
                      <Button className="btn-gold">
                        Upgrade to Inner Circle
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-white border border-[#E5E5E5] p-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2 text-[#0A0A0A]">
                <Bell size={20} className="text-[#C4A661]" />
                Email Notifications
              </h2>
              
              <div className="space-y-4">
                {/* Daily Devotional - Featured */}
                <label className="flex items-center justify-between p-5 bg-[#0A0A0A] border border-[#C4A661]/30 cursor-pointer hover:border-[#C4A661] transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#C4A661] flex items-center justify-center text-[#0A0A0A]">
                      <Cross size={20} weight="fill" />
                    </div>
                    <div>
                      <p className="font-medium text-[#F7F7F5]">Daily Devotional</p>
                      <p className="text-sm text-[#A3A3A3]">Receive scripture, reflection & action prompt every morning</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.devotional_email_enabled}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      setNotifications({ ...notifications, devotional_email_enabled: enabled });
                      try {
                        await axios.post(
                          `${API}/api/devotional/${enabled ? 'subscribe' : 'unsubscribe'}`,
                          {},
                          { headers: getAuthHeader() }
                        );
                        toast.success(enabled ? 'Subscribed to daily devotionals' : 'Unsubscribed from daily devotionals');
                      } catch (err) {
                        toast.error('Failed to update preference');
                        setNotifications({ ...notifications, devotional_email_enabled: !enabled });
                      }
                    }}
                    className="w-5 h-5 accent-[#C4A661]"
                  />
                </label>
                
                <label className="flex items-center justify-between p-5 bg-[#F5F5F0] border border-[#E5E5E5] cursor-pointer hover:border-[#C4A661] transition-colors">
                  <div>
                    <p className="font-medium text-[#0A0A0A]">Marketing Emails</p>
                    <p className="text-sm text-[#525252]">Receive tips, promotions, and special offers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.notification_email_marketing}
                    onChange={(e) => setNotifications({ ...notifications, notification_email_marketing: e.target.checked })}
                    className="w-5 h-5 accent-[#C4A661]"
                  />
                </label>
                
                <label className="flex items-center justify-between p-5 bg-[#F5F5F0] border border-[#E5E5E5] cursor-pointer hover:border-[#C4A661] transition-colors">
                  <div>
                    <p className="font-medium text-[#0A0A0A]">Community Updates</p>
                    <p className="text-sm text-[#525252]">Notifications about brotherhood activity</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.notification_email_community}
                    onChange={(e) => setNotifications({ ...notifications, notification_email_community: e.target.checked })}
                    className="w-5 h-5 accent-[#C4A661]"
                  />
                </label>
                
                <label className="flex items-center justify-between p-5 bg-[#F5F5F0] border border-[#E5E5E5] cursor-pointer hover:border-[#C4A661] transition-colors">
                  <div>
                    <p className="font-medium text-[#0A0A0A]">Product Updates</p>
                    <p className="text-sm text-[#525252]">New Kingdom training and platform updates</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.notification_email_updates}
                    onChange={(e) => setNotifications({ ...notifications, notification_email_updates: e.target.checked })}
                    className="w-5 h-5 accent-[#C4A661]"
                  />
                </label>
              </div>
              
              <Button
                onClick={handleUpdateNotifications}
                disabled={loading}
                className="mt-6 btn-gold"
                data-testid="save-notifications-btn"
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Referral Stats - Dark Section within Light */}
            <div className="bg-[#0A0A0A] border border-[#C4A661]/30 p-8 text-[#F7F7F5]">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
                <CurrencyDollar size={20} className="text-[#C4A661]" />
                Referral Program
              </h2>
              
              <p className="text-[#A3A3A3] mb-4">
                Earn <span className="text-[#C4A661] font-bold">${REFERRAL_COMMISSION}</span> for every brother you refer who joins the Inner Circle!
              </p>
              <p className="text-sm text-[#A3A3A3] italic mb-8">
                <Cross size={14} className="inline-block mr-1" weight="bold" />
                "He who walks with wise men will be wise." — Proverbs 13:20
              </p>
              
              {referralStats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="p-5 bg-[#141414] border border-white/10">
                      <p className="text-2xl font-bold text-[#F7F7F5]">{referralStats.total_referrals}</p>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-widest">Total Referrals</p>
                    </div>
                    <div className="p-5 bg-[#141414] border border-white/10">
                      <p className="text-2xl font-bold text-[#C4A661]">{referralStats.total_conversions}</p>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-widest">Conversions</p>
                    </div>
                    <div className="p-5 bg-[#141414] border border-white/10">
                      <p className="text-2xl font-bold text-emerald-400">${referralStats.total_earnings.toFixed(2)}</p>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-widest">Total Earned</p>
                    </div>
                    <div className="p-5 bg-[#141414] border border-white/10">
                      <p className="text-2xl font-bold text-amber-400">${referralStats.pending_earnings.toFixed(2)}</p>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-widest">Pending</p>
                    </div>
                  </div>
                  
                  {/* Referral Link */}
                  <div className="p-5 bg-[#141414] border border-[#C4A661]/30">
                    <Label className="text-[#C4A661] text-sm mb-3 block">Your Referral Link</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        value={referralStats.referral_link}
                        readOnly
                        className="bg-[#0A0A0A] border-white/10 text-[#F7F7F5] text-sm"
                      />
                      <Button
                        onClick={copyReferralLink}
                        className="btn-gold px-4"
                        data-testid="copy-referral-btn"
                      >
                        <Copy size={18} />
                      </Button>
                    </div>
                    <p className="text-xs text-[#A3A3A3] mt-3">
                      Share this link with brothers. When they join Inner Circle, you earn ${REFERRAL_COMMISSION}!
                    </p>
                  </div>
                </>
              )}
            </div>
            
            {/* Referral History - Light */}
            <div className="bg-white border border-[#E5E5E5] p-8">
              <h3 className="font-bold mb-6 flex items-center gap-2 text-[#0A0A0A]">
                <Users size={18} className="text-[#C4A661]" />
                Your Referrals
              </h3>
              
              {referralList.length > 0 ? (
                <div className="space-y-3">
                  {referralList.map(ref => (
                    <div key={ref.id} className="flex items-center justify-between p-4 bg-[#F5F5F0] border border-[#E5E5E5]">
                      <div>
                        <p className="text-[#0A0A0A] text-sm">{ref.referred_user_email}</p>
                        <p className="text-xs text-[#A3A3A3]">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-3 py-1 ${
                          ref.status === 'converted' || ref.status === 'paid_out'
                            ? 'bg-emerald-500/20 text-emerald-600 border border-emerald-500/30'
                            : 'bg-[#E5E5E5] text-[#525252] border border-[#E5E5E5]'
                        }`}>
                          {ref.status === 'signed_up' && 'Signed Up'}
                          {ref.status === 'converted' && `Converted • $${ref.commission_amount}`}
                          {ref.status === 'paid_out' && `Paid • $${ref.commission_amount}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A3A3A3] text-sm">No referrals yet. Share your link to start building together!</p>
              )}
            </div>
          </motion.div>
        )}
        </div>
      </div>

      {/* Photo Position Editor Modal */}
      {showPhotoEditor && getProfilePhotoUrl() && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowPhotoEditor(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-[#1A1A1A] border border-[#262626] rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#F7F7F5]">Adjust Photo Position</h3>
              <button
                onClick={() => setShowPhotoEditor(false)}
                className="text-[#A3A3A3] hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <p className="text-sm text-[#A3A3A3] mb-4">
              Drag the image to adjust how it appears in your profile frame.
            </p>
            
            {/* Preview Frame */}
            <div className="flex justify-center mb-6">
              <div 
                ref={photoEditorRef}
                className="w-48 h-48 rounded-full overflow-hidden border-4 border-[#C4A661] cursor-move select-none relative"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img 
                  src={getProfilePhotoUrl()} 
                  alt="Profile" 
                  className="w-[200%] h-[200%] object-cover pointer-events-none"
                  style={{
                    transform: `translate(${-(photoPosition.x)}%, ${-(photoPosition.y)}%)`
                  }}
                  draggable={false}
                />
                {/* Drag indicator overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-black/60 rounded-lg px-3 py-2 text-white text-xs font-medium">
                    Drag to adjust
                  </div>
                </div>
              </div>
            </div>
            
            {/* Position Controls */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2 block">
                  Horizontal Position
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={photoPosition.x}
                  onChange={(e) => setPhotoPosition(prev => ({ ...prev, x: Number(e.target.value) }))}
                  className="w-full h-2 bg-[#262626] rounded-lg appearance-none cursor-pointer accent-[#C4A661]"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2 block">
                  Vertical Position
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={photoPosition.y}
                  onChange={(e) => setPhotoPosition(prev => ({ ...prev, y: Number(e.target.value) }))}
                  className="w-full h-2 bg-[#262626] rounded-lg appearance-none cursor-pointer accent-[#C4A661]"
                />
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => {
                  setPhotoPosition({ x: 50, y: 50 });
                }}
                className="flex-1 bg-[#262626] hover:bg-[#363636] text-white"
              >
                Reset
              </Button>
              <Button
                type="button"
                onClick={savePhotoPosition}
                className="flex-1 bg-[#C4A661] hover:bg-[#D4B671] text-[#0A0A0A] font-bold"
              >
                Save Position
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Footer - Dark */}
      <footer className="bg-[#0A0A0A] border-t border-[#262626] py-8">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#C4A661] font-bold mb-2">POWR</p>
          <p className="text-sm text-[#525252]">Purpose. Ownership. Wealth. Responsibility.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProfilePage;
